import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Base URL for API requests
const API_BASE_URL = `${API_BASE}/api/blogs`;

// Types
interface BlogContentBlock {
  type: 'paragraph' | 'heading' | 'image' | 'list' | 'quote' | 'split_section';
  value: any;
  imageCaption?: string;
  image?: string;
  layout?: 'left' | 'right';
}

interface Blog {
  _id: string;
  title: string;
  slug: string;
  content: BlogContentBlock[];
  excerpt: string;
  tags: string[];
  status: 'draft' | 'published';
  featuredImage?: string;
  image?: string; // Client uses 'image' for featured image
  author: string;
  category: string;
  readTime: string;
  rating?: number;
  commentCount?: number;
  createdAt: string;
  publishedAt?: string;
}

interface BlogFormData {
  title: string;
  content: BlogContentBlock[];
  excerpt: string;
  tags: string;
  status: 'draft' | 'published';
  featuredImage: string;
  author: string;
  category: string;
  readTime: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface BlogsResponse {
  success: boolean;
  count: number;
  total: number;
  pagination: PaginationData;
  data: Blog[];
}

interface BlogResponse {
  success: boolean;
  data: Blog;
}


// Props types
interface BlogListProps {
  blogs: Blog[];
  onEdit: (blog: Blog) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (blog: Blog) => void;
  onRefresh: () => void;
}

interface BlogFormProps {
  blog: Blog | null;
  onSubmit: (blogData: Omit<Blog, '_id' | 'createdAt' | 'slug'>) => Promise<void>;
  onCancel: () => void;
}

// Blog List Component
const BlogList: React.FC<BlogListProps> = ({ blogs, onEdit, onDelete, onToggleStatus, onRefresh }) => {
  return (
    <div className="bg-transparent">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto shadow-xl shadow-gray-200/40 rounded-2xl border border-gray-100 bg-white">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50/50">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Post Info</th>
              <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
              <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Metrics</th>
              <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
              <th scope="col" className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-50">
            {blogs.map((blog) => (
              <tr key={blog._id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-sm font-black text-gray-900 uppercase tracking-tight truncate max-w-xs">{blog.title}</div>
                  <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest truncate max-w-xs">{blog.author}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${blog.status === 'published' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-yellow-50 text-yellow-600 border border-yellow-100'
                    }`}>
                    {blog.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-4 text-[11px] font-bold text-gray-500 uppercase">
                    <span className="flex flex-col">
                      <span className="text-gray-300 text-[8px]">Rating</span>
                      <span className="text-blue-600">{blog.rating ? blog.rating.toFixed(1) : '0.0'}</span>
                    </span>
                    <span className="flex flex-col">
                      <span className="text-gray-300 text-[8px]">Comments</span>
                      <span className="text-purple-600">{blog.commentCount || 0}</span>
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-[11px] font-black text-gray-600 uppercase tracking-widest">
                    {new Date(blog.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onToggleStatus(blog)}
                      className={`p-2 rounded-lg transition-colors font-bold text-[10px] uppercase tracking-widest ${blog.status === 'published' ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                        }`}
                    >
                      {blog.status === 'published' ? 'Draft' : 'Publish'}
                    </button>
                    <button onClick={() => onEdit(blog)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      Edit
                    </button>
                    <button onClick={() => onDelete(blog._id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                      Del
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {blogs.map((blog) => (
          <div key={blog._id} className="bg-white p-4 rounded-2xl shadow-lg shadow-gray-200/50 border border-gray-100 relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full -mr-8 -mt-8 opacity-10 transition-transform group-hover:scale-110 ${blog.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}`}></div>

            <div className="flex justify-between items-start mb-3 relative z-10">
              <div className="min-w-0 pr-4">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight truncate leading-tight mb-1">{blog.title}</h3>
                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">{blog.author}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter shrink-0 ${blog.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {blog.status}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50/50 rounded-xl border border-gray-100/50 mb-4 relative z-10">
              <div className="text-center">
                <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5 tracking-widest">Rating</span>
                <p className="text-xs font-black text-blue-600">{blog.rating ? blog.rating.toFixed(1) : '0.0'}</p>
              </div>
              <div className="text-center border-x border-gray-200/50">
                <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5 tracking-widest">Comments</span>
                <p className="text-xs font-black text-purple-600">{blog.commentCount || 0}</p>
              </div>
              <div className="text-center">
                <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5 tracking-widest">Created</span>
                <p className="text-[10px] font-black text-gray-700 uppercase">{new Date(blog.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 relative z-10">
              <button
                onClick={() => onToggleStatus(blog)}
                className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg border active:scale-95 transition-all ${blog.status === 'published' ? 'text-orange-600 bg-orange-50 border-orange-100' : 'text-green-600 bg-green-50 border-green-100'
                  }`}
              >
                {blog.status === 'published' ? 'Set Draft' : 'Publish Now'}
              </button>
              <button
                onClick={() => onEdit(blog)}
                className="flex-1 py-2 text-[10px] font-black uppercase tracking-widest text-[#523680] bg-purple-50 rounded-lg border border-purple-100 active:scale-95 transition-all"
              >
                Edit Post
              </button>
              <button
                onClick={() => onDelete(blog._id)}
                className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-red-400 bg-red-50 rounded-lg border border-red-50 active:scale-95 transition-all"
              >
                Del
              </button>
            </div>
          </div>
        ))}
        {blogs.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-400 italic font-medium uppercase text-xs tracking-widest">No blog posts found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Form Component
interface BlogFormProps {
  blog: Blog | null;
  onSubmit: (blogData: Omit<Blog, '_id' | 'createdAt' | 'slug'>) => Promise<void>;
  onCancel: () => void;
}

const BlogForm: React.FC<BlogFormProps> = ({ blog, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    content: [],
    excerpt: '',
    tags: '',
    status: 'draft',
    featuredImage: '',
    author: '',
    category: 'Market News',
    readTime: '5 min read'
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [slugPreview, setSlugPreview] = useState<string>('');

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        content: Array.isArray(blog.content) ? blog.content : [],
        excerpt: blog.excerpt || '',
        tags: blog.tags ? blog.tags.join(', ') : '',
        status: blog.status || 'draft',
        featuredImage: blog.featuredImage || blog.image || '',
        author: blog.author || '',
        category: blog.category || 'Market News',
        readTime: blog.readTime || '5 min read'
      });
    }
  }, [blog]);

  useEffect(() => {
    const generatedSlug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setSlugPreview(generatedSlug);
  }, [formData.title]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };

  // Content Block Handlers
  const addBlock = (type: BlogContentBlock['type']) => {
    const newBlock: BlogContentBlock = { type, value: '' };

    if (type === 'list') {
      newBlock.value = [''];
    } else if (type === 'split_section') {
      newBlock.layout = 'left';
      newBlock.image = '';
      newBlock.value = {
        title: '',
        description: '',
        subTitle: '',
        list: [''],
        footer: ''
      };
    } else if (type === 'image') {
      newBlock.imageCaption = '';
    }

    setFormData({
      ...formData,
      content: [...formData.content, newBlock]
    });
  };

  const removeBlock = (index: number) => {
    const newContent = [...formData.content];
    newContent.splice(index, 1);
    setFormData({ ...formData, content: newContent });
  };

  const updateBlock = (index: number, updates: Partial<BlogContentBlock>) => {
    const newContent = [...formData.content];
    newContent[index] = { ...newContent[index], ...updates };
    setFormData({ ...formData, content: newContent });
  };

  const updateBlockValue = (index: number, value: any) => {
    const newContent = [...formData.content];
    newContent[index].value = value;
    setFormData({ ...formData, content: newContent });
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === formData.content.length - 1)) return;
    const newContent = [...formData.content];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newContent[index], newContent[targetIndex]] = [newContent[targetIndex], newContent[index]];
    setFormData({ ...formData, content: newContent });
  };

  const handleBlockImageUpload = async (index: number, file: File) => {
    try {
      // 1. Get signed URL
      const { data: signedData } = await axios.post(`${API_BASE_URL}/getSignedUrl`, {
        files: [{ fileName: file.name, fileType: file.type }]
      });

      if (signedData.success) {
        const { uploadUrl, publicUrl } = signedData.data.signedUrls[0];

        // 2. Upload to S3
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type }
        });

        // 3. Update state
        if (formData.content[index].type === 'split_section') {
          updateBlock(index, { image: publicUrl });
        } else {
          updateBlockValue(index, publicUrl);
        }
      }
    } catch (error) {
      console.error('S3 Upload Error:', error);
      toast.error('Failed to upload block image');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let imageUrl = formData.featuredImage;

      // 1. Upload featured image if new file selected
      if (imageFile) {
        const { data: signedData } = await axios.post(`${API_BASE_URL}/getSignedUrl`, {
          files: [{ fileName: imageFile.name, fileType: imageFile.type }]
        });

        if (signedData.success) {
          const { uploadUrl, publicUrl } = signedData.data.signedUrls[0];

          await axios.put(uploadUrl, imageFile, {
            headers: { 'Content-Type': imageFile.type }
          });
          imageUrl = publicUrl;
        }
      }

      const blogData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        status: formData.status,
        featuredImage: imageUrl,
        image: imageUrl, // for compatibility
        author: formData.author || 'Admin',
        category: formData.category,
        readTime: formData.readTime,
        slug: slugPreview
      };

      await onSubmit(blogData);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save blog post');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">{blog ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Author</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            >
              <option value="Market News">Market News</option>
              <option value="Beginner Friendly">Beginner Friendly</option>
              <option value="Trust & Transparency">Trust & Transparency</option>
              <option value="Investment Tips">Investment Tips</option>
              <option value="Retirement Planning">Retirement Planning</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Read Time</label>
            <input
              type="text"
              name="readTime"
              value={formData.readTime}
              onChange={handleChange}
              placeholder="e.g. 5 min read"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2">Excerpt (Brief Description)</label>
          <textarea
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            rows={2}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2">Tags (comma separated)</label>
          <input
            type="text"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="e.g. Finance, Business, Trends"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2">Featured Image</label>
          <input
            type="file"
            onChange={handleImageChange}
            className="mb-2 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            accept="image/*"
          />
          {formData.featuredImage && (
            <img src={formData.featuredImage} alt="Featured" className="h-24 object-cover rounded mt-2 border" />
          )}
        </div>

        <hr className="my-8" />

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800">Content Blocks</h3>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => addBlock('paragraph')} className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm font-semibold hover:bg-blue-200">+ Paragraph</button>
              <button type="button" onClick={() => addBlock('heading')} className="bg-purple-100 text-purple-700 px-3 py-1 rounded text-sm font-semibold hover:bg-purple-200">+ Heading</button>
              <button type="button" onClick={() => addBlock('image')} className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm font-semibold hover:bg-green-200">+ Image</button>
              <button type="button" onClick={() => addBlock('list')} className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded text-sm font-semibold hover:bg-yellow-200">+ List</button>
              <button type="button" onClick={() => addBlock('quote')} className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm font-semibold hover:bg-red-200">+ Quote</button>
              <button type="button" onClick={() => addBlock('split_section')} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded text-sm font-semibold hover:bg-indigo-200">+ Split Section</button>
            </div>
          </div>

          <div className="space-y-6">
            {formData.content.map((block, index) => (
              <div key={index} className="bg-gray-50 border rounded-xl p-4 relative group">
                <div className="absolute right-4 top-4 flex gap-2">
                  <button type="button" onClick={() => moveBlock(index, 'up')} className="text-gray-400 hover:text-indigo-600">↑</button>
                  <button type="button" onClick={() => moveBlock(index, 'down')} className="text-gray-400 hover:text-indigo-600">↓</button>
                  <button type="button" onClick={() => removeBlock(index)} className="text-gray-400 hover:text-red-600 font-bold ml-2">×</button>
                </div>

                <div className="mb-2">
                  <span className="text-[10px] uppercase font-bold text-gray-400 px-2 py-1 bg-white border rounded tracking-widest leading-none">
                    {block.type}
                  </span>
                </div>

                {/* Render specific inputs based on type */}
                {block.type === 'paragraph' && (
                  <textarea
                    value={block.value}
                    onChange={(e) => updateBlockValue(index, e.target.value)}
                    placeholder="Enter paragraph text..."
                    rows={4}
                    className="w-full border rounded p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                )}

                {block.type === 'heading' && (
                  <input
                    type="text"
                    value={block.value}
                    onChange={(e) => updateBlockValue(index, e.target.value)}
                    placeholder="Enter heading text..."
                    className="w-full border rounded p-3 font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                )}

                {block.type === 'image' && (
                  <div className="space-y-3">
                    <input
                      type="file"
                      onChange={(e) => e.target.files?.[0] && handleBlockImageUpload(index, e.target.files[0])}
                      className="text-sm text-gray-500 w-full"
                    />
                    {block.value && <img src={block.value} alt="Block" className="h-32 rounded border" />}
                    <input
                      type="text"
                      value={block.imageCaption || ''}
                      onChange={(e) => updateBlock(index, { imageCaption: e.target.value })}
                      placeholder="Image caption (optional)"
                      className="w-full border rounded p-2 text-sm italic"
                    />
                  </div>
                )}

                {block.type === 'list' && (
                  <div className="space-y-2">
                    {(block.value as string[]).map((item, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const newList = [...block.value];
                            newList[i] = e.target.value;
                            updateBlockValue(index, newList);
                          }}
                          className="flex-grow border rounded p-2 text-sm"
                          placeholder={`List item ${i + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newList = [...block.value];
                            newList.splice(i, 1);
                            updateBlockValue(index, newList);
                          }}
                          className="text-red-500 px-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateBlockValue(index, [...block.value, ''])}
                      className="text-xs text-indigo-600 font-bold hover:underline"
                    >
                      + Add List Item
                    </button>
                  </div>
                )}

                {block.type === 'quote' && (
                  <textarea
                    value={block.value}
                    onChange={(e) => updateBlockValue(index, e.target.value)}
                    placeholder="Enter quote here..."
                    rows={3}
                    className="w-full border rounded p-3 italic text-indigo-800 bg-indigo-50 border-indigo-100"
                  />
                )}

                {block.type === 'split_section' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <label className="text-xs font-bold text-gray-500">SECTION IMAGE</label>
                      <input
                        type="file"
                        onChange={(e) => e.target.files?.[0] && handleBlockImageUpload(index, e.target.files[0])}
                        className="text-xs text-gray-500"
                      />
                      {block.image && <img src={block.image} alt="Split Section" className="h-32 object-cover rounded border" />}

                      <label className="text-xs font-bold text-gray-500 block mt-4">LAYOUT</label>
                      <select
                        value={block.layout}
                        onChange={(e) => updateBlock(index, { layout: e.target.value as 'left' | 'right' })}
                        className="border rounded p-2 text-sm w-full"
                      >
                        <option value="left">Image on Left</option>
                        <option value="right">Image on Right</option>
                      </select>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={block.value.title}
                        onChange={(e) => updateBlockValue(index, { ...block.value, title: e.target.value })}
                        placeholder="Section Title"
                        className="w-full border rounded p-2 text-lg font-bold"
                      />
                      <textarea
                        value={block.value.description}
                        onChange={(e) => updateBlockValue(index, { ...block.value, description: e.target.value })}
                        placeholder="Section Description"
                        rows={3}
                        className="w-full border rounded p-2 text-sm"
                      />
                      <input
                        type="text"
                        value={block.value.subTitle}
                        onChange={(e) => updateBlockValue(index, { ...block.value, subTitle: e.target.value })}
                        placeholder="Sub-title for list"
                        className="w-full border rounded p-2 text-sm font-semibold"
                      />
                      <div className="bg-white p-2 border rounded">
                        {block.value.list.map((li: string, i: number) => (
                          <div key={i} className="flex mb-1">
                            <input
                              type="text"
                              value={li}
                              onChange={(e) => {
                                const newList = [...block.value.list];
                                newList[i] = e.target.value;
                                updateBlockValue(index, { ...block.value, list: newList });
                              }}
                              className="flex-grow border-none text-xs p-1"
                              placeholder="Detail point..."
                            />
                          </div>
                        ))}
                        <button type="button" onClick={() => updateBlockValue(index, { ...block.value, list: [...block.value.list, ''] })} className="text-[10px] text-indigo-600">+ Add Point</button>
                      </div>
                      <input
                        type="text"
                        value={block.value.footer}
                        onChange={(e) => updateBlockValue(index, { ...block.value, footer: e.target.value })}
                        placeholder="Section Footer Text"
                        className="w-full border rounded p-2 text-xs italic"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {formData.content.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed rounded-xl border-gray-200">
              <p className="text-gray-400">No content blocks yet. Add some blocks to build your post.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-12 pt-8 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-[#523680] hover:bg-indigo-700 text-white font-bold py-2 px-10 rounded focus:outline-none focus:shadow-outline ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isSubmitting ? 'Saving...' : (blog ? 'Update Blog Post' : 'Create Blog Post')}
          </button>
        </div>
      </form>
    </div>
  );
};

// Main Blogs Component
const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentBlog, setCurrentBlog] = useState<Blog | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Fetch unique tags
  const fetchTags = async (): Promise<void> => {
    try {
      const response = await axios.get<{ success: boolean; data: string[] }>(`${API_BASE_URL}/tags`);
      if (response.data.success) {
        setAvailableTags(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch blogs
  const fetchBlogs = async (): Promise<void> => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}?page=${page}&limit=10`;

      if (searchTerm) url += `&search=${searchTerm}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (tagFilter) url += `&tag=${tagFilter}`;

      const response = await axios.get<BlogsResponse>(url);

      if (response.data.success) {
        setBlogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError('Failed to fetch blogs');
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [page, searchTerm, statusFilter, tagFilter]);

  // Create blog
  const createBlog = async (blogData: Omit<Blog, '_id' | 'createdAt' | 'slug'>): Promise<void> => {
    try {
      const response = await axios.post<BlogResponse>(API_BASE_URL, blogData);

      if (response.data.success) {
        toast.success('Blog created successfully');
        setShowForm(false);
        fetchBlogs();
      }
    } catch (error: any) {
      console.error('Error creating blog:', error);
      if (error.response?.data?.error) {
        // Handle array of error messages
        if (Array.isArray(error.response.data.error)) {
          toast.error(error.response.data.error.join(', '));
        } else {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error('Failed to create blog');
      }
      throw error;
    }
  };

  // Update blog
  const updateBlog = async (blogData: Omit<Blog, '_id' | 'createdAt' | 'slug'>): Promise<void> => {
    try {
      if (!currentBlog) throw new Error("No blog selected for update");

      const response = await axios.put<BlogResponse>(`${API_BASE_URL}/${currentBlog._id}`, blogData);

      if (response.data.success) {
        toast.success('Blog updated successfully');
        setShowForm(false);
        setCurrentBlog(null);
        fetchBlogs();
      }
    } catch (error: any) {
      console.error('Error updating blog:', error);
      if (error.response?.data?.error) {
        // Handle array of error messages
        if (Array.isArray(error.response.data.error)) {
          toast.error(error.response.data.error.join(', '));
        } else {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error('Failed to update blog');
      }
      throw error;
    }
  };

  // Delete blog
  const deleteBlog = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const response = await axios.delete<{ success: boolean }>(`${API_BASE_URL}/${id}`);

      if (response.data.success) {
        toast.success('Blog deleted successfully');
        fetchBlogs();
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  // Toggle blog status
  const handleToggleStatus = async (blog: Blog): Promise<void> => {
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      const response = await axios.put(`${API_BASE_URL}/${blog._id}`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success(`Blog ${newStatus === 'published' ? 'published' : 'moved to draft'} successfully`);
        fetchBlogs();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle blog submission (create or update)
  const handleSubmit = async (blogData: Omit<Blog, '_id' | 'createdAt' | 'slug'>): Promise<void> => {
    if (currentBlog) {
      await updateBlog(blogData);
    } else {
      await createBlog(blogData);
    }
  };

  // Handle edit action
  const handleEdit = (blog: Blog): void => {
    setCurrentBlog(blog);
    setShowForm(true);
  };

  // Handle create new blog action
  const handleCreateNew = (): void => {
    setCurrentBlog(null);
    setShowForm(true);
  };

  // Handle form cancel
  const handleCancel = (): void => {
    setShowForm(false);
    setCurrentBlog(null);
  };

  // Handle page change
  const handlePageChange = (newPage: number): void => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle search
  const handleSearch = (e: FormEvent): void => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
    fetchBlogs();
  };

  // Handle filter changes
  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    if (name === 'status') setStatusFilter(value);
    if (name === 'tag') setTagFilter(value);
    setPage(1); // Reset to first page on filter change
  };

  // Clear filters
  const clearFilters = (): void => {
    setSearchTerm('');
    setStatusFilter('');
    setTagFilter('');
    setPage(1);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <ToastContainer />

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Blog Management</h1>
        {!showForm && (
          <button
            onClick={handleCreateNew}
            className="bg-[#523680] hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Create New Blog
          </button>
        )}
      </div>

      {/* Form or List */}
      {showForm ? (
        <BlogForm
          blog={currentBlog}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      ) : (
        <div>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-md mb-6">
            <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
              <div className="flex-grow min-w-[200px]">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="search">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  placeholder="Search blogs..."
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div className="w-40">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={statusFilter}
                  onChange={handleFilterChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">All</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div className="w-40">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="tag">
                  Tag
                </label>
                <select
                  id="tag"
                  name="tag"
                  value={tagFilter}
                  onChange={handleFilterChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">All</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-[#523680] hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          {/* Blog List */}
          {loading ? (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error! </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          ) : blogs.length === 0 ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
              <p>No blogs found. Create your first blog post!</p>
            </div>
          ) : (
            <>
              <BlogList
                blogs={blogs}
                onEdit={handleEdit}
                onDelete={deleteBlog}
                onToggleStatus={handleToggleStatus}
                onRefresh={fetchBlogs}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                  <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      Previous
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handlePageChange(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === i + 1
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Blogs;